import { Helmet } from 'react-helmet-async';
import { OG_IMAGES } from '@/constants/seoImages';

interface ProductSchemaProps {
  name: string;
  description: string;
  price?: string;
  currency?: string;
  availability?: string;
  brand?: string;
  image?: string;
  reviews?: Array<{
    author: string;
    rating: number;
    reviewBody: string;
    datePublished?: string;
  }>;
}

export const ProductSchema = ({
  name,
  description,
  price = "0",
  currency = "USD",
  availability = "https://schema.org/InStock",
  brand = "SeniorThrive",
  image = OG_IMAGES.default,
  reviews = []
}: ProductSchemaProps) => {
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": name,
    "description": description,
    "brand": {
      "@type": "Brand",
      "name": brand
    },
    "image": image,
    "applicationCategory": "HealthApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": price,
      "priceCurrency": currency,
      "availability": availability,
      "priceValidUntil": "2026-12-31",
      "seller": {
        "@type": "Organization",
        "name": brand
      }
    },
    "aggregateRating": reviews.length > 0 ? {
      "@type": "AggregateRating",
      "ratingValue": (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1),
      "reviewCount": reviews.length,
      "bestRating": 5,
      "worstRating": 1
    } : undefined,
    "review": reviews.map(review => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": review.author
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.rating,
        "bestRating": 5
      },
      "reviewBody": review.reviewBody,
      "datePublished": review.datePublished || new Date().toISOString()
    })),
    "featureList": [
      "Smart Home Safety Checks",
      "Wellness Tracking with ThriveScore",
      "Family Care Coordination",
      "Fall Prevention Analytics",
      "Medication Management",
      "Emergency Response Planning",
      "Community Engagement Tools"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(productSchema)}
      </script>
    </Helmet>
  );
};